import { ReactComponent as CategoryIcon } from '@assets/components/panel/monitoring/category.svg';
import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { Box, Center, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { groupBy } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { criticalityOrder, monitoringTypeFromSensorType, SensorOrSubSensorInfo } from '../monitoring.types';
import { getSensorCriticality, sortSensorByType } from '../monitoring.utils';
import { SortEnum, useMonitoringContext } from '../MonitoringContext';
import SensorCard from './SensorCard';

function sortByCriticality(
  sensorOrSubSensorInfo1: SensorOrSubSensorInfo,
  sensorOrSubSensorInfo2: SensorOrSubSensorInfo,
) {
  const criticality1 = getSensorCriticality(sensorOrSubSensorInfo1);
  const criticality2 = getSensorCriticality(sensorOrSubSensorInfo2);
  return criticalityOrder.indexOf(criticality1) - criticalityOrder.indexOf(criticality2);
}

export default function TypeList() {
  const { filteredSensorOrSubSensorInfoList } = useMonitoringContext();

  const groupedSensorsByType = Object.entries(
    groupBy(
      filteredSensorOrSubSensorInfoList,
      (filteredSensorOrSubSensorInfo) => monitoringTypeFromSensorType[filteredSensorOrSubSensorInfo.configuration.type],
    ),
  )
    .map(([type, sensorOrSubSensorInfoList]) => {
      return {
        type,
        sensors: sensorOrSubSensorInfoList.toSorted((sensorOrSubSensorInfo1, sensorOrSubSensorInfo2) => {
          const result = sortByCriticality(sensorOrSubSensorInfo1, sensorOrSubSensorInfo2);
          if (result === 0) {
            return sortSensorByType(sensorOrSubSensorInfo1.configuration, sensorOrSubSensorInfo2.configuration);
          }
          return result;
        }),
      };
    })
    .sort((a, b) => a.type.localeCompare(b.type));

  if (filteredSensorOrSubSensorInfoList.length === 0) {
    return (
      <Center height="100%" width="100%">
        <VStack gap={3}>
          <EmptyPanelDroneIcon />
          <Text
            textStyle="lg"
            fontFamily="space"
            fontFeatureSettings="'liga' off"
            color="neutral.200"
            maxWidth="230px"
            textAlign="center"
            userSelect="none"
          >
            <FormattedMessage id="components.monitoring.noResult" />
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <For each={groupedSensorsByType}>
      {({ type, sensors }, index) => (
        <HStack
          gap={2}
          alignItems="start"
          width="100%"
          key={type}
          marginBottom={index === Object.entries(groupedSensorsByType).length - 1 ? 6 : 0}
        >
          <Box position="relative">
            <Icon as={CategoryIcon} width="88px" height="88px" />
            <Text position="absolute" left="50%" top="50%" transform="translate(-50%, -50%)">
              {type}
            </Text>
          </Box>
          <HStack gap={2} flexWrap="wrap" width="100%">
            <For
              each={sensors}
              fallback={
                <Center border="2px solid" width="100%" height="88px" borderColor="neutral.600">
                  <Text>
                    <FormattedMessage id="components.monitoring.type.noSensor" />
                  </Text>
                </Center>
              }
            >
              {(sensorOrSubSensorInfo) => (
                <SensorCard
                  sensorOrSubSensorInfo={sensorOrSubSensorInfo}
                  type={SortEnum.TYPE}
                  key={sensorOrSubSensorInfo.configuration.uniqueKey}
                />
              )}
            </For>
          </HStack>
        </HStack>
      )}
    </For>
  );
}
